// React import not required in newer JSX runtimes

interface AssetsListProps {
  assets: {
    players?: number[];
    picks?: number[];
    goalies?: number[];
  };
  compact?: boolean;
  playerNames?: { [key: number]: string };
  pickDetails?: { [key: number]: { round: number; year?: number | null; description?: string; overall_pick?: number | null } };
}

export function AssetsList({ 
  assets, 
  compact = false, 
  playerNames = {}, 
  pickDetails = {} 
}: AssetsListProps) {
  if (!assets) {
    return <div className="text-sm opacity-60">Aucun élément</div>;
  }

  const { players = [], picks = [], goalies = [] } = assets;
  const totalItems = players.length + picks.length + goalies.length;

  if (totalItems === 0) {
    return <div className="text-sm opacity-60">Aucun élément</div>;
  }

  if (compact) {
    return (
      <div className="text-sm space-y-1">
        {players.length > 0 && (
          <div className="flex items-center gap-1">
            <span></span>
            <div className="flex flex-wrap gap-1">
              {players.map((playerId, index) => (
                <span key={playerId}>
                  {playerNames[playerId] || `Joueur ${playerId}`}
                  {index < players.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
        {goalies.length > 0 && (
          <div className="flex items-center gap-1">
            <span></span>
            <div className="flex flex-wrap gap-1">
              {goalies.map((goalieId, index) => (
                <span key={goalieId}>
                  {playerNames[goalieId] || `Gardien ${goalieId}`}
                  {index < goalies.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
        {picks.length > 0 && (
          <div className="flex items-center gap-1">
            <span>🎯</span>
            <div className="flex flex-wrap gap-1">
              {picks.map((pickId, index) => (
                <span key={pickId}>
                  {pickDetails[pickId]?.description || `Choix ${pickId}`}
                  {index < picks.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-base-300 p-3 rounded text-sm space-y-2">
      {players.length > 0 && (
        <div>
          <div className="font-semibold text-primary mb-1">
            🏒 Joueurs ({players.length})
          </div>
          <div className="ml-3 space-y-1">
            {players.map(playerId => (
              <div key={playerId} className="flex items-center gap-2">
                <span className="badge badge-sm badge-outline">#{playerId}</span>
                <span className="opacity-80">
                  {playerNames[playerId] || `Joueur ${playerId}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {goalies.length > 0 && (
        <div>
          <div className="font-semibold text-secondary mb-1">
            Gardiens ({goalies.length})
          </div>
          <div className="ml-3 space-y-1">
            {goalies.map(goalieId => (
              <div key={goalieId} className="flex items-center gap-2">
                <span className="badge badge-sm badge-outline">#{goalieId}</span>
                <span className="opacity-80">
                  {playerNames[goalieId] || `Gardien ${goalieId}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {picks.length > 0 && (
        <div>
          <div className="font-semibold text-accent mb-1">
            🎯 Choix de Draft ({picks.length})
          </div>
          <div className="ml-3 space-y-1">
            {picks.map(pickId => (
              <div key={pickId} className="flex items-center gap-2">
                <span className="badge badge-sm badge-outline">#{pickId}</span>
                <span className="opacity-80">
                  {pickDetails[pickId]?.description || 
                    (pickDetails[pickId] ? 
                      `${pickDetails[pickId].round}e ronde ${pickDetails[pickId].year}` +
                      (pickDetails[pickId].overall_pick ? ` (#${pickDetails[pickId].overall_pick})` : '')
                      : `Choix ${pickId}`
                    )
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}